/**
  NewsPage
 */

import React from 'react';
import { LinkType, StandardImage } from 'types/global';
import {
	AltCardWrapper,
	Container,
	ImageColumn,
	ImageColumnInner,
	DateArea,
	Heading,
	BottomArea,
	BottomListArea,
	BottomListAreaLeftContainer,
	AnchorTextAlt,
	Preamble,
	IconBadgeAlt,
	Date,
	Category,
} from './Cards.styles';
import Image from 'components/Image';
import Icon from 'components/Icon';
import Video from './Video';

interface Props {
	/** NewsPage heading */
	heading: string;

	/** NewsPage preamble */
	preamble?: string;

	/** NewsPage date */
	date: string;

	/** NewsPage category */
	category: string;

	/** NewsPage link */
	link: LinkType;

	/** NewsPage video */
	video?: boolean;

	/** NewsPage image */
	image: StandardImage;

	/** Parent backgroundcolor */
	backgroundColor: 'lightBlue' | 'blue' | 'white';

	/** Card index */
	index: number;

	/** Card id */
	id?: string;

	/** EpiFind hitId */
	hitId?: string;

	hideImages?: boolean;

	showIntroduction?: boolean;

	usePreambleFallback?: boolean;

	/** Whether to render the news item as a list instead of a cards */
	showAsList?: boolean;

	isLatestNewsItem?: boolean;
}

/** Container component for NewsPageCard. */
const NewsPageCard: React.FC<Props> = ({
	date,
	category,
	heading,
	preamble,
	link,
	video,
	backgroundColor,
	image,
	hideImages,
	showIntroduction,
	usePreambleFallback,
	index,
	id,
	hitId,
	showAsList = false,
	isLatestNewsItem,
}) => (
	<AltCardWrapper
		backgroundColor={backgroundColor}
		noImage={hideImages}
		showAsList={showAsList}
		aria-labelledby={`heading-${id}`}
	>
		<Container
			href={link.link && typeof link.link === 'string' ? link.link : link}
			id={hitId || id}
			showaslist={showAsList.toString()}
		>
			{!hideImages && (
				<ImageColumn>
					<ImageColumnInner>
						{video ? (
							<Video image={image} />
						) : (
							<Image
								src={image.src}
								alt={image.alt}
								srcSet={`
							${image.sizes[0]} 500w,
							${image.sizes[1]} 350w
						`}
								webpSrcSet={`
							${image.webpSizes[0]} 500w,
							${image.webpSizes[1]} 350w
						`}
								sizes="(min-width: 768px) 60vw, (max-width: 767px) 70vw, 350px"
							/>
						)}
					</ImageColumnInner>
				</ImageColumn>
			)}

			{!showAsList ? (
				<BottomArea>
					<DateArea>
						<Date showAsList={showAsList}>{date}</Date>
						<Category showAsList={showAsList}>{category}</Category>
					</DateArea>

					<Heading as="h3" id={`heading-${id}`} showAsList={showAsList}>
						{heading}
					</Heading>

					{(showIntroduction || usePreambleFallback) && preamble && (
						<Preamble backgroundColor={backgroundColor}>{preamble}</Preamble>
					)}
					<AnchorTextAlt showAsList={showAsList}>
						{link.text}
						<IconBadgeAlt>
							<Icon icon={'arrowRight'} size={0} aria-hidden="true" />
						</IconBadgeAlt>
					</AnchorTextAlt>
				</BottomArea>
			) : (
				<BottomListArea isLatestNewsItem={isLatestNewsItem}>
					<BottomListAreaLeftContainer>
						<DateArea>
							<Date showAsList={showAsList}>{date}</Date>
							<Category showAsList={showAsList}>{category}</Category>
						</DateArea>

						<Heading as="h3" id={`heading-${id}`} showAsList={showAsList}>
							{heading}
						</Heading>

						{(showIntroduction || usePreambleFallback) && preamble && (
							<Preamble backgroundColor={backgroundColor}>{preamble}</Preamble>
						)}
					</BottomListAreaLeftContainer>
					<AnchorTextAlt showAsList={showAsList}>
						{link.text}
						<IconBadgeAlt>
							<Icon icon={'arrowRight'} size={0} aria-hidden="true" />
						</IconBadgeAlt>
					</AnchorTextAlt>
				</BottomListArea>
			)}
		</Container>
	</AltCardWrapper>
);

export default NewsPageCard;
